import { useCallback, useEffect, useRef } from 'react';
import { type SubscriptionTerm, type UserSubscription } from '@cx-sdk/subs';
import { type SubsProduct } from '@cx-web/domain-types';
import { type CXLoginSuccessEvent } from '@cx-web/feature-auth/events';
import { retryOperation } from '@cx-web/utils/retryOperation';
import { z } from 'zod';
import { createZodFetcher } from 'zod-fetch';

import { type Repub } from '@cx-modules/constants';

import { URL_PARAMS } from '~/apps/product-plus/constants/url-params';

export type Selection = {
  productId: string;
};

const fetchWithZod = createZodFetcher();

function buildCheckoutUrls({
  canUpgrade,
  priceFormatted,
  redirectPath,
  term,
}: {
  canUpgrade?: boolean;
  priceFormatted: string;
  redirectPath?: string | null;
  term?: SubscriptionTerm;
}) {
  const successUrl = new URL(window.location.href);
  successUrl.searchParams.set(URL_PARAMS.IS_UPGRADE, String(canUpgrade));
  successUrl.searchParams.set(URL_PARAMS.VERIFY_PAYMENT, 'true');
  successUrl.searchParams.set(URL_PARAMS.PRICE, encodeURIComponent(priceFormatted));
  if (term) successUrl.searchParams.set(URL_PARAMS.TERM, term);
  if (redirectPath) successUrl.searchParams.set(URL_PARAMS.REDIRECT_TO, redirectPath);

  const cancelUrl = new URL(window.location.href);
  cancelUrl.searchParams.set(URL_PARAMS.CANCELLED_PAYMENT, 'true');
  cancelUrl.searchParams.set(URL_PARAMS.IS_UPGRADE, String(canUpgrade));
  if (redirectPath) cancelUrl.searchParams.set(URL_PARAMS.REDIRECT_TO, redirectPath);

  return { successUrl: successUrl.toString(), cancelUrl: cancelUrl.toString() };
}

async function verifySubscriptionStatus(repub: Repub): Promise<boolean> {
  const subscriptions = await fetchWithZod(z.array(z.custom<UserSubscription>()), '/api/cx/subscriptions', {
    method: 'GET',
    headers: { 'OC-Repub': repub },
  });
  return subscriptions.length > 0;
}

export const useSubscribe = ({
  canUpgrade,
  cancelPayment,
  onCancel,
  onConfirmed,
  onError,
  onSubscribe,
  onVerifying,
  redirectPath,
  repub,
  shouldLogin,
  userHasCancelledSubscription,
  verifyPayment,
}: {
  canUpgrade?: boolean;
  cancelPayment: string | null;
  onCancel?: () => void;
  onConfirmed?: () => void;
  onError?: () => void;
  onSubscribe?: (product: SubsProduct) => void;
  onVerifying?: () => void;
  redirectPath?: string;
  repub: Repub;
  shouldLogin: boolean;
  userHasCancelledSubscription: boolean;
  verifyPayment: string | null;
}): {
  getPortalUrl: () => Promise<string>;
  subscribe: (product: SubsProduct, userData?: CXLoginSuccessEvent['detail']) => void;
} => {
  const verifyRef = useRef(false);
  const abortRef = useRef(false);
  const checkoutInFlightRef = useRef(false);

  const getCheckoutUrl = useCallback(
    async (
      { coupon, id: priceId, name, priceFormatted, term, type }: SubsProduct,
      userData?: CXLoginSuccessEvent['detail'], // only exists when user have just logged in
    ) => {
      const { cancelUrl, successUrl } = buildCheckoutUrls({
        canUpgrade,
        term,
        priceFormatted,
        redirectPath,
      });

      const userSubscriptionStatusContainsCancelled =
        (userData?.subscriptions ?? []).some(({ status }) => status === 'CANCELLED') ?? false;

      const isEssentialProduct = name.toLowerCase().includes('essential');
      const shouldIncludeTrialDays =
        !userSubscriptionStatusContainsCancelled && !isEssentialProduct && !userHasCancelledSubscription;

      return await fetchWithZod(z.string(), '/api/cx/subscriptions/checkout', {
        method: 'POST',
        headers: {
          'OC-Repub': repub,
        },
        body: JSON.stringify({
          priceId,
          type,
          urls: {
            successUrl,
            cancelUrl,
          },
          ...(shouldIncludeTrialDays ? { trialDays: 7 } : {}),
          ...(userData ? { authToken: userData.token, deviceId: userData.deviceId } : {}),
          ...(coupon ? { couponCode: coupon.id } : {}),
        }),
      });
    },
    [canUpgrade, redirectPath, repub],
  );

  const getPortalUrl = useCallback(async () => {
    const returnUrl = new URL(window.location.href);
    return await fetchWithZod(z.string(), '/api/cx/subscriptions/portal', {
      method: 'POST',
      headers: {
        'OC-Repub': repub,
      },
      body: JSON.stringify({ returnUrl: returnUrl.toString() }),
    });
  }, [repub]);

  const initiateCheckout = useCallback(
    async (product: SubsProduct, userData?: CXLoginSuccessEvent['detail']): Promise<void> => {
      const checkoutUrl = await getCheckoutUrl(product, userData);
      window.location.href = checkoutUrl ?? `${window.location.origin}/${redirectPath ?? ''}`;
    },
    [getCheckoutUrl, redirectPath],
  );

  const checkout = useCallback(
    async (product: SubsProduct, userData?: CXLoginSuccessEvent['detail']) => {
      if (checkoutInFlightRef.current) return;
      checkoutInFlightRef.current = true;

      try {
        await initiateCheckout(product, userData);
      } catch {
        onError?.();
      } finally {
        checkoutInFlightRef.current = false;
      }
    },
    [initiateCheckout, onError],
  );

  const subscribe = useCallback(
    async (product: SubsProduct, userData?: CXLoginSuccessEvent['detail']) => {
      onSubscribe?.(product);
      await checkout(product, userData);
    },
    [checkout, onSubscribe],
  );

  const verify = useCallback(async () => {
    onVerifying?.();

    try {
      const isSubscribed = await retryOperation(() => verifySubscriptionStatus(repub), 1000, 10, abortRef);

      if (!isSubscribed) throw new Error('User is not subscribed');

      onConfirmed?.();
    } catch {
      onError?.();
    }
  }, [onConfirmed, onError, onVerifying, repub]);

  const cancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  const shouldVerify = verifyPayment && !shouldLogin;
  const shouldCancel = cancelPayment && !shouldLogin;

  useEffect(() => {
    if (shouldVerify && !verifyRef.current) {
      verify();
      verifyRef.current = true;
      return () => {
        abortRef.current = true;
      };
    }
  }, [shouldVerify, verify]);

  useEffect(() => {
    if (shouldCancel) cancel();
  }, [shouldCancel, cancel]);

  return {
    getPortalUrl,
    subscribe,
  };
};
